import React, { useState, useEffect } from 'react';
import {
  Typography,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Chip,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { contextAPI } from '../../services/api/context';
import { PrivacySettings as PrivacySettingsType } from '../../types/context';

interface PrivacySettingsProps {
  userId: string;
}

const PrivacySettings: React.FC<PrivacySettingsProps> = ({ userId }) => {
  const [settings, setSettings] = useState<PrivacySettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [newSource, setNewSource] = useState('');


  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await contextAPI.getPrivacySettings();
      setSettings(response.data.settings);
    } catch (err: any) {
      setError('Failed to load privacy settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      await contextAPI.updatePrivacySettings(settings);
      setSuccess('Privacy settings updated successfully');
    } catch (err: any) {
      setError('Failed to save privacy settings');
    } finally {
      setSaving(false);
    }
  };

  const handleExportData = async () => {
    try {
      const response = await contextAPI.exportPrivacyData();
      setSuccess(`Data export initiated. Export ID: ${response.data.exportId}`);
      setExportDialogOpen(false);
    } catch (err: any) {
      setError('Failed to export data');
    }
  };

  const handleDeleteData = async () => {
    try {
      await contextAPI.deletePrivacyData({});
      setSuccess('Your context data has been deleted');
      setDeleteDialogOpen(false);
    } catch (err: any) {
      setError('Failed to delete data');
    }
  };

  const addAllowedSource = () => {
    if (!settings || !newSource) return;

    setSettings({
      ...settings,
      context_collection: {
        ...settings.context_collection,
        allowed_sources: [...settings.context_collection.allowed_sources, newSource],
      },
    });
    setNewSource('');
  };

  const removeAllowedSource = (source: string) => {
    if (!settings) return;

    setSettings({
      ...settings,
      context_collection: {
        ...settings.context_collection,
        allowed_sources: settings.context_collection.allowed_sources.filter(s => s !== source),
      },
    });
  };


  const removeExcludedSource = (source: string) => {
    if (!settings) return;

    setSettings({
      ...settings,
      context_collection: {
        ...settings.context_collection,
        excluded_sources: settings.context_collection.excluded_sources.filter(s => s !== source),
      },
    });
  };

  if (loading) {
    return <Typography>Loading privacy settings...</Typography>;
  }

  if (!settings) {
    return <Alert severity="error">Failed to load privacy settings</Alert>;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
        <SecurityIcon style={{ marginRight: 16, fontSize: 32 }} />
        <Typography variant="h5">Privacy & Data Control</Typography>
      </div>

      {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}
      {success && <Alert severity="success" style={{ marginBottom: 16 }}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Context Collection Settings */}
        <Grid item xs={12}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Context Collection</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <div>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.context_collection.enabled}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          context_collection: {
                            ...settings.context_collection,
                            enabled: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Enable context collection"
                />
                
                <div style={{ marginTop: 24 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Allowed Sources
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Only collect context data from these sources
                  </Typography>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                    {settings.context_collection.allowed_sources.map(source => (
                      <Chip
                        key={source}
                        label={source}
                        onDelete={() => removeAllowedSource(source)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <TextField
                      size="small"
                      placeholder="Add source"
                      value={newSource}
                      onChange={(e) => setNewSource(e.target.value)}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={addAllowedSource}
                      disabled={!newSource}
                    >
                      Add
                    </Button>
                  </div>
                </div>

                <div style={{ marginTop: 24 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Excluded Sources
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Never collect data from these sources
                  </Typography>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {settings.context_collection.excluded_sources.map(source => (
                      <Chip
                        key={source}
                        label={source}
                        onDelete={() => removeExcludedSource(source)}
                        color="error"
                        variant="outlined"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Data Retention Settings */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Data Retention</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <div>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Retention Period (days)"
                      value={settings.data_retention.days}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          data_retention: {
                            ...settings.data_retention,
                            days: parseInt(e.target.value) || 30,
                          },
                        })
                      }
                      InputProps={{ inputProps: { min: 1, max: 365 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.data_retention.auto_delete}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              data_retention: {
                                ...settings.data_retention,
                                auto_delete: e.target.checked,
                              },
                            })
                          }
                        />
                      }
                      label="Automatically delete old data"
                    />
                  </Grid>
                </Grid>
              </div>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Data Sharing Settings */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Data Sharing</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <div>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.data_sharing.share_with_agents}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          data_sharing: {
                            ...settings.data_sharing,
                            share_with_agents: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Share context with AI agents"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.data_sharing.share_activity_patterns}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          data_sharing: {
                            ...settings.data_sharing,
                            share_activity_patterns: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Share activity patterns for insights"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.data_sharing.share_anonymized}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          data_sharing: {
                            ...settings.data_sharing,
                            share_anonymized: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Share anonymized data for research"
                />
              </div>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Sensitive Data Settings */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Sensitive Data Protection</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <div>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.sensitive_data.mask_locations}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          sensitive_data: {
                            ...settings.sensitive_data,
                            mask_locations: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Mask location data"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.sensitive_data.mask_app_names}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          sensitive_data: {
                            ...settings.sensitive_data,
                            mask_app_names: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Mask application names"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.sensitive_data.mask_content}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          sensitive_data: {
                            ...settings.sensitive_data,
                            mask_content: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Mask content details"
                />
              </div>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<DownloadIcon />}
                onClick={() => setExportDialogOpen(true)}
                style={{ marginRight: 16 }}
              >
                Export My Data
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete My Data
              </Button>
            </div>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveSettings}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </Grid>
      </Grid>

      {/* Export Data Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
        <DialogTitle>Export Your Data</DialogTitle>
        <DialogContent>
          <Typography>
            You can export all your context data in a machine-readable format. 
            This includes all collected events, activity patterns, and insights.
          </Typography>
          <Alert severity="info" style={{ marginTop: 16 }}>
            The export will be prepared and you'll receive an email with the download link when ready.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleExportData}>
            Export Data
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Data Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Your Data</DialogTitle>
        <DialogContent>
          <Alert severity="warning" style={{ marginBottom: 16 }}>
            This action cannot be undone!
          </Alert>
          <Typography>
            Are you sure you want to delete all your context data? This includes:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="All collected context events" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Activity patterns and insights" />
            </ListItem>
            <ListItem>
              <ListItemText primary="ML model training data" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Custom adaptation rules" />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteData}>
            Delete All Data
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PrivacySettings;